import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Code as CodeIcon,
  WrapText as WrapTextIcon,
  FormatListNumbered as LineNumbersIcon,
} from '@mui/icons-material';
import { TextViewerProps, ViewerError } from '../../../types/viewer.types';
import { useDirectContent } from '../../../hooks/useDirectContent';

/**
 * Text Viewer component with syntax highlighting and formatting options
 */
const TextViewer: React.FC<TextViewerProps> = ({
  contentId,
  metadata,
  onError,
  onLoad,
  language,
  theme = 'light',
  showLineNumbers = false,
  wordWrap = true,
  readOnly = true,
  highlightSyntax = false,
  className,
  style,
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [lineNumbers, setLineNumbers] = useState<boolean>(showLineNumbers);
  const [wordWrapEnabled, setWordWrapEnabled] = useState<boolean>(wordWrap);
  const [syntaxHighlighting, setSyntaxHighlighting] = useState<boolean>(highlightSyntax);

  const { contentUrl, isDirectDownload } = useDirectContent(contentId, undefined, metadata);

  // Load text content
  useEffect(() => {
    const loadText = async () => {
      if (!contentUrl) return;

      setLoading(true);
      try {
        console.log('Loading text content:', contentId, 'via', isDirectDownload ? 'presigned URL' : 'streaming');

        // Fetch content from URL (works for both presigned and streaming URLs)
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
        onLoad?.();
      } catch (error: any) {
        console.error('Failed to load text:', error);
        const viewerError: ViewerError = {
          code: 'STREAMING_ERROR',
          message: 'Failed to load text content',
          details: error,
          retryable: true,
        };
        onError?.(viewerError);
      } finally {
        setLoading(false);
      }
    };

    loadText();
  }, [contentId, contentUrl, isDirectDownload, onLoad, onError]);

  // Detect programming language from file extension or content
  const detectLanguage = useCallback((): string => {
    if (language) return language;
    
    const fileName = metadata.fileName.toLowerCase();
    const extension = fileName.split('.').pop();
    
    const languageMap: Record<string, string> = {
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      py: 'python',
      java: 'java',
      cpp: 'cpp',
      c: 'c',
      h: 'c',
      css: 'css',
      html: 'html',
      xml: 'xml',
      json: 'json',
      yml: 'yaml',
      yaml: 'yaml',
      md: 'markdown',
      sql: 'sql',
      sh: 'bash',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
    };

    return languageMap[extension || ''] || 'text';
  }, [language, metadata.fileName]);

  // Generate line numbers
  const generateLineNumbers = useCallback((text: string): string[] => {
    return text.split('\n').map((_, index) => (index + 1).toString().padStart(3, ' '));
  }, []);

  // Apply basic syntax highlighting (simple regex-based approach)
  const applySyntaxHighlighting = useCallback((text: string, lang: string): string => {
    if (!syntaxHighlighting) return text;

    // Simple keyword highlighting for common languages
    const keywordPatterns: Record<string, RegExp[]> = {
      javascript: [
        /\b(const|let|var|function|return|if|else|for|while|class|export|import)\b/g,
        /\b(true|false|null|undefined)\b/g,
        /"[^"]*"/g, // strings
        /'[^']*'/g, // strings
        /\/\/.*$/gm, // comments
        /\/\*[\s\S]*?\*\//g, // multiline comments
      ],
      python: [
        /\b(def|class|if|elif|else|for|while|import|from|return|True|False|None)\b/g,
        /"[^"]*"/g, // strings
        /'[^']*'/g, // strings
        /#.*$/gm, // comments
      ],
      // Add more languages as needed
    };

    let highlightedText = text;
    const patterns = keywordPatterns[lang] || [];
    
    patterns.forEach((pattern, index) => {
      const className = index === 0 ? 'keyword' : 
                      index === 1 ? 'literal' :
                      index >= 2 && index <= 3 ? 'string' : 'comment';
      
      highlightedText = highlightedText.replace(pattern, (match) => 
        `<span class="${className}">${match}</span>`
      );
    });

    return highlightedText;
  }, [syntaxHighlighting]);

  if (loading) {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="body2" color="text.secondary">
          Loading text content...
        </Typography>
      </Box>
    );
  }

  const detectedLanguage = detectLanguage();
  const lines = generateLineNumbers(content);
  const highlightedContent = applySyntaxHighlighting(content, detectedLanguage);

  return (
    <Box
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Text Viewer Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {detectedLanguage.toUpperCase()} • {content.split('\n').length} lines • {content.length} chars
        </Typography>

        <ToggleButtonGroup size="small" sx={{ gap: 0.5 }}>
          <Tooltip title="Line Numbers">
            <ToggleButton
              value="lineNumbers"
              selected={lineNumbers}
              onChange={() => setLineNumbers(!lineNumbers)}
            >
              <LineNumbersIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Word Wrap">
            <ToggleButton
              value="wordWrap"
              selected={wordWrapEnabled}
              onChange={() => setWordWrapEnabled(!wordWrapEnabled)}
            >
              <WrapTextIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Syntax Highlighting">
            <ToggleButton
              value="syntax"
              selected={syntaxHighlighting}
              onChange={() => setSyntaxHighlighting(!syntaxHighlighting)}
            >
              <CodeIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* Text Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            m: 1,
            p: 0,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            fontSize: '14px',
            lineHeight: 1.5,
            height: 'calc(100% - 16px)',
            overflow: 'auto',
            display: 'flex',
          }}
        >
          {/* Line Numbers */}
          {lineNumbers && (
            <Box
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                px: 1,
                py: 2,
                minWidth: '50px',
                textAlign: 'right',
                color: 'text.secondary',
                userSelect: 'none',
                whiteSpace: 'pre',
                fontSize: '13px',
              }}
            >
              {lines.join('\n')}
            </Box>
          )}

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              whiteSpace: wordWrapEnabled ? 'pre-wrap' : 'pre',
              wordBreak: wordWrapEnabled ? 'break-word' : 'normal',
              overflow: wordWrapEnabled ? 'auto' : 'scroll',
            }}
            dangerouslySetInnerHTML={
              syntaxHighlighting
                ? { __html: highlightedContent }
                : undefined
            }
          >
            {!syntaxHighlighting && content}
          </Box>
        </Paper>

        {/* Syntax highlighting styles */}
        {syntaxHighlighting && (
          <style>
            {`
              .keyword { color: #007acc; font-weight: bold; }
              .literal { color: #0451a5; }
              .string { color: #a31515; }
              .comment { color: #008000; font-style: italic; }
            `}
          </style>
        )}
      </Box>
    </Box>
  );
};

export default TextViewer;